import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text, Stack, IconButton } from 'office-ui-fabric-react';
import moment from 'moment';
import Module from '../Module';
import ModuleContent from '../ModuleContent';
import SubModule from '../SubModule';
import SubModuleContent from '../SubModuleContent';
import SubModuleHeader from '../SubModuleHeader';
import TextButton from '../TextButton';
import FilterDetails, { calculationPrefix } from '../FilterDetails';
import styles from './AgreementCard.style';
import { expandRateAssignments } from '../RoyaltyFeeForm/RoyaltyFeeForm';

const stackTokens = {
  childrenGap: 40,
};

const AgreementCard = ({
  agreement,
  enableUpArrow,
  enableDownArrow,
  upArrowClicked,
  downArrowClicked,
  editFeeClicked,
  ...props
}) => {
  const filters = useMemo(() => {
    let rateAssignments = agreement.rateAssignments || [];

    if (agreement.tieringMethod) {
      rateAssignments = expandRateAssignments(rateAssignments);
    }

    return rateAssignments.map(rateAssignment => ({
      ...rateAssignment,
      _id: rateAssignment.filterId,
      tieringMethod: agreement.tieringMethod,
    }));
  }, [agreement]);

  return (
    <Module {...props} className={styles.filterList}>
      <ModuleContent>
        <SubModule>
          <SubModuleHeader id={agreement.address} variant="large">
            <IconButton
              iconProps={{
                iconName: 'Up',
              }}
              disabled={!enableUpArrow}
              onClick={upArrowClicked}
              className={styles.upButton}
              data-testid="agreementCardUpArrowButton"
              ariaLabel="Up arrow"
              title="Up arrow"
              tabIndex={0}
            />
            <IconButton
              iconProps={{
                iconName: 'Down',
              }}
              disabled={!enableDownArrow}
              onClick={downArrowClicked}
              className={styles.downButton}
              data-testid="agreementCardDownArrowButton"
              ariaLabel="Down arrow"
              title="Down arrow"
              tabIndex={0}
            />
            <span data-testid="agreementCardName">{agreement.name}</span>
          </SubModuleHeader>
          <SubModuleContent>
            <Stack>
              <Text className={styles.calcType}>
                Calculation Method: <strong>{agreement.calculation.name}</strong>
              </Text>
              {agreement.tieringMethod && (
                <Stack horizontal tokens={stackTokens}>
                  <Text className={styles.calcType}>
                    Tiering Method:{' '}
                    <strong>{agreement.tieringMethodFriendly || agreement.tieringMethod}</strong>
                  </Text>
                  {agreement.tieringResetStartDate && (
                    <Text className={styles.calcType}>
                      Tiering Reset Interval:{' '}
                      <strong>
                        {agreement.tieringResetIntervalFriendly || agreement.tieringResetInterval}
                      </strong>
                    </Text>
                  )}
                  {agreement.tieringResetStartDate && (
                    <Text className={styles.calcType}>
                      Tiering Reset Start Date:{' '}
                      <strong>
                        {moment
                          .utc(agreement.tieringResetStartDate, true)
                          .add(1, 'year')
                          .format('YYYY-MM-DD')}
                      </strong>
                    </Text>
                  )}
                </Stack>
              )}
            </Stack>

            <FilterDetails filters={filters} prefix={calculationPrefix(agreement.calculation)} />

            <Stack horizontalAlign="end">
              <TextButton onClick={() => editFeeClicked(agreement)}>Edit Fee</TextButton>
            </Stack>
          </SubModuleContent>
        </SubModule>
      </ModuleContent>
    </Module>
  );
};

AgreementCard.propTypes = {
  enableUpArrow: PropTypes.bool.isRequired,
  enableDownArrow: PropTypes.bool.isRequired,
  upArrowClicked: PropTypes.func.isRequired,
  downArrowClicked: PropTypes.func.isRequired,
  editFeeClicked: PropTypes.func.isRequired,
  agreement: PropTypes.shape({
    name: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
    tieringMethod: PropTypes.string,
    tieringMethodFriendly: PropTypes.string,
    tieringResetInterval: PropTypes.string,
    tieringResetIntervalFriendly: PropTypes.string,
    tieringResetStartDate: PropTypes.string,
    calculation: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    rateAssignments: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
};

export default AgreementCard;
